import React, { useState, useEffect } from 'react';
import api from '../api';
import Table from '../components/Table';
import Modal from '../components/Modal';
import ImageUpload from '../components/ImageUpload';
import { FiPlus, FiCalendar, FiTag, FiUser, FiFileText, FiImage } from 'react-icons/fi';
import toast from 'react-hot-toast';

const FormGroup = ({ children, cols = 1 }) => {
  const colClasses = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-3',
  };
  return (
    <div className={`grid ${colClasses[cols]} gap-6 mb-6`}>
      {children}
    </div>
  );
};

const InputField = ({ icon, label, name, type = 'text', value, onChange, required = true }) => (
  <div>
    <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
      {label}
    </label>
    <div className="mt-1 relative rounded-md shadow-sm">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        {icon}
      </div>
      <input
        type={type}
        name={name}
        id={name}
        value={value}
        onChange={onChange}
        className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
        required={required}
      />
    </div>
  </div>
);
const News = () => {
  const [news, setNews] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentNews, setCurrentNews] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    author: '',
    tags: '',
    images: [],
    publishDate: '',
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchNews();
  }, [pagination.page, pagination.limit, searchTerm]);

  const fetchNews = async () => {
    try {
      const response = await api.get('/news', {
        params: {
          page: pagination.page,
          limit: pagination.limit,
          search: searchTerm,
        },
      });
      setNews(response.data.data);
      setPagination(prevState => ({
        ...prevState,
        total: response.data.pagination.total,
        pages: response.data.pagination.pages,
      }));
    } catch (error) {
      toast.error('Failed to fetch news');
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prevState => ({ ...prevState, page: newPage }));
  };

  const handleLimitChange = (newLimit) => {
    setPagination(prevState => ({ ...prevState, limit: newLimit, page: 1 }));
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setPagination(prevState => ({ ...prevState, page: 1 }));
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImageUpload = (urls) => {
    setFormData(prevState => ({
      ...prevState,
      images: [...prevState.images, ...urls]
    }));
  };

  const handleImageRemove = (index) => {
    setFormData(prevState => ({
      ...prevState,
      images: prevState.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        tags: formData.tags.split(',').map(tag => tag.trim()),
      };
      if (currentNews) {
        await api.put(`/news/${currentNews._id}`, data);
        toast.success('News updated successfully');
      } else {
        await api.post('/news', data);
        toast.success('News created successfully');
      }
      setIsModalOpen(false);
      fetchNews();
    } catch (error) {
      toast.error('Failed to save news');
    }
  };

  const handleEdit = (news) => {
    setCurrentNews(news);
    setFormData({
      title: news.title,
      content: news.content,
      author: news.author,
      tags: news.tags.join(', '),
      images: news.images,
      publishDate: news.publishDate.split('T')[0],
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this news?')) {
      try {
        await api.delete(`/news/${id}`);
        toast.success('News deleted successfully');
        fetchNews();
      } catch (error) {
        toast.error('Failed to delete news');
      }
    }
  };

  const columns = [
    { key: 'title', label: 'Title' },
    { key: 'author', label: 'Author' },
    { key: 'publishDate', label: 'Publish Date', render: (item) => new Date(item.publishDate).toLocaleDateString() },
    { key: 'tags', label: 'Tags', render: (item) => item.tags.join(', ') },
  ];

 

  return (
    <div>
    <div className="flex justify-between items-center mb-6">
      <h1 className="text-3xl font-bold text-gray-900">News</h1>
      <button
        onClick={() => {
          setCurrentNews(null);
          setFormData({
            title: '',
            content: '',
            author: '',
            tags: '',
            images: [],
            publishDate: '',
          });
          setIsModalOpen(true);
        }}
        className="px-4 py-2 font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-150 ease-in-out"
      >
        <FiPlus className="inline-block mr-2" />
        Add News
      </button>
    </div>
    <Table
      columns={columns}
      data={news}
      onEdit={handleEdit}
      onDelete={handleDelete}
      pagination={pagination}
      onPageChange={handlePageChange}
      onLimitChange={handleLimitChange}
      onSearch={handleSearch}
    />
    <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={currentNews ? 'Edit News' : 'Add News'} size="xl">
        <form onSubmit={handleSubmit} className="space-y-6">
          <FormGroup cols={2}>
            <InputField
              icon={<FiFileText className="h-5 w-5 text-gray-400" />}
              label="Title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
            />
            <InputField
              icon={<FiUser className="h-5 w-5 text-gray-400" />}
              label="Author"
              name="author"
              value={formData.author}
              onChange={handleInputChange}
            />
          </FormGroup>
          <FormGroup>
            <div>
              <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-1">
                Content
              </label>
              <textarea
                name="content"
                id="content"
                rows="4"
                value={formData.content}
                onChange={handleInputChange}
                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                required
              ></textarea>
            </div>
          </FormGroup>
          <FormGroup cols={2}>
            <InputField
              icon={<FiTag className="h-5 w-5 text-gray-400" />}
              label="Tags (comma-separated)"
              name="tags"
              value={formData.tags}
              onChange={handleInputChange}
            />
            <InputField
              icon={<FiCalendar className="h-5 w-5 text-gray-400" />}
              label="Publish Date"
              name="publishDate"
              type="date"
              value={formData.publishDate}
              onChange={handleInputChange}
            />
          </FormGroup>
          <FormGroup>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Images</label>
              <ImageUpload
                onUpload={handleImageUpload}
                onRemove={handleImageRemove}
                images={formData.images}
                multiple
              />
            </div>
          </FormGroup>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="mr-3 px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              {currentNews ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default News;